/**
 * MeDrawer — per-device prefs panel. Music vol, SFX vol, mute, haptics,
 * reduce-shake, color-blind palette. Stored in localStorage via @tt/audio
 * for audio prefs, and direct localStorage for accessibility prefs.
 */
import { useState } from 'react';
import { audio } from '@tt/audio';
import { useEscapeClose } from '../../lib/useEscapeClose';

interface Props { open: boolean; onClose: () => void }

const SHAKE_KEY = 'tt:a11y:shake';
const CB_KEY = 'tt:a11y:colorblind';

type ShakeLevel = 0 | 1 | 2 | 3;
type CbMode = 'off' | 'deuteranope' | 'protanope' | 'tritanope';

function loadShake(): ShakeLevel {
  try {
    const v = Number(localStorage.getItem(SHAKE_KEY));
    if ([0, 1, 2, 3].includes(v)) return v as ShakeLevel;
  } catch { /* */ }
  return 3;
}
function saveShake(v: ShakeLevel) {
  try { localStorage.setItem(SHAKE_KEY, String(v)); document.documentElement.dataset['shake'] = String(v); } catch { /* */ }
}
function loadCb(): CbMode {
  try {
    const v = localStorage.getItem(CB_KEY);
    if (v === 'deuteranope' || v === 'protanope' || v === 'tritanope') return v;
  } catch { /* */ }
  return 'off';
}
function saveCb(v: CbMode) {
  try { localStorage.setItem(CB_KEY, v); document.documentElement.dataset['cb'] = v; } catch { /* */ }
}

export function MeDrawer({ open, onClose }: Props) {
  const initial = audio.prefs.get();
  const [musicVol, setMusicVol] = useState(initial.musicVol);
  const [sfxVol, setSfxVol] = useState(initial.sfxVol);
  const [ambienceVol, setAmbienceVol] = useState(initial.ambienceVol);
  const [muted, setMuted] = useState(initial.muted);
  const [haptics, setHaptics] = useState(initial.haptics);
  const [shake, setShake] = useState<ShakeLevel>(loadShake);
  const [cb, setCb] = useState<CbMode>(loadCb);
  useEscapeClose(open, onClose);

  if (!open) return null;
  return (
    <div className="settings-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="settings-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="settings-header">
          <h2 className="display">Your settings</h2>
          <button type="button" className="link" onClick={onClose}>close</button>
        </header>
        <div className="me-drawer">
          <section className="me-section">
            <h3>Sound</h3>
            <div className="me-row">
              <span>Mute everything</span>
              <input
                type="checkbox"
                checked={muted}
                onChange={(e) => { setMuted(e.target.checked); audio.prefs.setMuted(e.target.checked); }}
              />
            </div>
            <div className="me-row">
              <span>Music</span>
              <input
                type="range" min={0} max={1} step={0.01}
                value={musicVol}
                onChange={(e) => { const v = Number(e.target.value); setMusicVol(v); audio.prefs.setMusicVol(v); }}
                className="me-slider"
                aria-label="music volume"
                disabled={muted}
              />
            </div>
            <div className="me-row">
              <span>Game sounds</span>
              <input
                type="range" min={0} max={1} step={0.01}
                value={sfxVol}
                onChange={(e) => { const v = Number(e.target.value); setSfxVol(v); audio.prefs.setSfxVol(v); }}
                className="me-slider"
                aria-label="sfx volume"
                disabled={muted}
              />
            </div>
            <div className="me-row">
              <span>Ambience</span>
              <input
                type="range" min={0} max={1} step={0.01}
                value={ambienceVol}
                onChange={(e) => { const v = Number(e.target.value); setAmbienceVol(v); audio.prefs.setAmbienceVol(v); }}
                className="me-slider"
                aria-label="ambience volume"
                disabled={muted}
              />
            </div>
            <div className="me-row">
              <span>Haptics (phone vibrate)</span>
              <input
                type="checkbox"
                checked={haptics}
                onChange={(e) => { setHaptics(e.target.checked); audio.prefs.setHaptics(e.target.checked); }}
              />
            </div>
          </section>
          <section className="me-section">
            <h3>Accessibility</h3>
            <div className="me-row">
              <span>Screen shake</span>
              <select
                value={shake}
                onChange={(e) => { const v = Number(e.target.value) as ShakeLevel; setShake(v); saveShake(v); }}
                className="setting-input"
                aria-label="screen shake intensity"
              >
                <option value={0}>Off</option>
                <option value={1}>Low</option>
                <option value={2}>Medium</option>
                <option value={3}>Full</option>
              </select>
            </div>
            <div className="me-row">
              <span>Color-blind palette</span>
              <select
                value={cb}
                onChange={(e) => { const v = e.target.value as CbMode; setCb(v); saveCb(v); }}
                className="setting-input"
                aria-label="color-blind palette"
              >
                <option value="off">Off</option>
                <option value="deuteranope">Deuteranope</option>
                <option value="protanope">Protanope</option>
                <option value="tritanope">Tritanope</option>
              </select>
            </div>
            <p className="muted" style={{ fontSize: '0.78rem', margin: '4px 0 0' }}>
              Palette presets are wiring-only today; full per-game palette swaps land in UI-3.
            </p>
          </section>
          <section className="me-section">
            <h3>About</h3>
            <p className="muted" style={{ fontSize: '0.8rem' }}>
              Tile Tavern · v0.5 · Audio engine procedural (no asset loads).
              No accounts, no tracking. Tap the gear in a room for lobby-wide settings.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
